import {Component, computed, inject, OnInit, signal, viewChild} from '@angular/core';
import {BasicTaDetails, FullTaDetails, UserService} from '../../../api';
import {TaSubmissionDatasource} from '../../../datasources/ta-submission.datasource';
import {TaAppStore} from '../../../state/clcta-store';
import {Router} from '@angular/router';
import {Button} from 'primeng/button';
import {Card} from 'primeng/card';
import {Chip} from 'primeng/chip';
import {DatePipe, NgClass} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {InputText} from 'primeng/inputtext';
import {TableModule} from 'primeng/table';
import {toTAStatusText} from '../../../util/app.types';
import {LocationService} from '../../../services/location.service';
import {TaInfoCardComponent} from '../../components/ta-info-card/ta-info-card.component';
import {ConfirmationWrapperService} from '../../../services/confirmation-wrapper.service';
import {TaListBase} from '../ta-list-base.directive';
import {PrepaidIndicatorComponent} from '../../components/prepaid-indicator/prepaid-indicator.component';
import {AttachmentIndicatorComponent} from '../../components/attachment-indicator/attachment-indicator.component';
import {TaCostBubbleComponent} from '../../components/ta-cost-bubble/ta-cost-bubble.component';
import {TabsModule} from 'primeng/tabs';
import {ApprovalHistoryListComponent} from '../../components/ta-list-tables/approval-history-list/approval-history-list.component';
import {ApproverScopeListComponent} from '../../components/ta-list-tables/approver-scope-list/approver-scope-list.component';
import {TaCommentCount} from '../../components/ta-comments/ta-comment-count';

@Component({
  selector: 'clc-approver-page',
    imports: [
        Button,
        Card,
        Chip,
        DatePipe,
        FormsModule,
        InputText,
        TableModule,
        NgClass,
        TaInfoCardComponent,
        PrepaidIndicatorComponent,
        AttachmentIndicatorComponent,
        TaCostBubbleComponent,
        TabsModule,
        ApprovalHistoryListComponent,
        ApproverScopeListComponent,
        TaCommentCount,
    ],
  templateUrl: './clc-approver-page.html',
  styleUrl: './clc-approver-page.scss'
})
export class ClcApproverPage extends TaListBase {
  userApi = inject(UserService);
  locationService = inject(LocationService);
  confirmationServiceWrapper = inject(ConfirmationWrapperService);

  tabOption: string = 'pendingApproval';

  // Only resolves while a scope tab is the active one - the tab panels are lazy.
  private scopeList = viewChild(ApproverScopeListComponent);

  // The scope tabs overlap the pending queue, so an approval actioned there has to invalidate it.
  onScopeApproval(): void {
    this.getApprovals();
  }

  override ngOnInit() {
    super.ngOnInit()
    this.store.resetTaState();
    this.store.updateBreadCrumbData({
      title: 'Approvals Dashboard',
      titleIcon: 'fa-light fa-list-check fa-lg'
    });

    this.taSubmissionDatasource.resetSource();

    this.getApprovals();

    this.locationService.loadLocations();
  }

  reloadTaList(): void {
    this.getApprovals();
  }

  // The two tabs share the info card, so the selection from the tab being left would otherwise
  // stay on screen against the other tab's list.
  resetSelectedTa(): void {
    this.store.resetTaState();
  }

  getApprovals(): void {
    this.store.resetTaState();

    this.loadingService.showLoading({message: 'Loading Required Approvals', targetElement: this.loadingRef});
    this.userApi.getUserApprovals().subscribe({
      next: result => {
        if (result.data) {
          this.availableTaList.set(result.data.sort((a, b) => {
            const da = a.created?.created ? new Date(a.created.created).getTime() : 0;
            const db = b.created?.created ? new Date(b.created.created).getTime() : 0;
            return db - da; // newest first
          }))
        } else {
          console.error('No approvals TA list found.');
        }
        this.loadingService.hideLoading();
      },
      error: err => {
        console.error(err);
        this.loadingService.hideLoading();
      }
    });
  }

  canApproveTaSubmission(taSubmission: BasicTaDetails): boolean {
    return taSubmission.status === 'AcquittalComplete' || taSubmission.status === 'PendingApproval'
      || taSubmission.status === 'PendingCancelApproval';
  }

  approveTaSubmission(taSubmission: BasicTaDetails | FullTaDetails | undefined): void {
    if (!taSubmission) {
      return;
    }
    if (taSubmission.status === 'AcquittalComplete') {
      this.confirmationServiceWrapper.approveAcquittalConfirm("approve", "", taSubmission).subscribe({
        next: () => {
          this.refreshAfterApproval();
        },
        error: err => {
          console.error(err);
        },
        complete: () => {
          // console.log('Approve completed.');
        }
      });
    } else if (taSubmission.status === 'PendingApproval') {
      this.confirmationServiceWrapper.approveConfirm("approve", "", taSubmission).subscribe({
        next: () => {
          this.refreshAfterApproval();
        },
        error: err => {
          console.error(err);
        },
        complete: () => {
          // console.log('Approve completed.');
        }
      });
    } else if (taSubmission.status === 'PendingCancelApproval') {
      // Signing off the cancellation of a pre-paid TA that was already paid out. Approve is the
      // only action here - the server offers no withhold or reject for a cancellation.
      this.confirmationServiceWrapper.approveCancellationConfirm("", taSubmission).subscribe({
        next: () => {
          this.refreshAfterApproval();
        },
        error: err => {
          console.error(err);
        },
      });
    }
  }

  // The shared info card can action an approval from any tab except the history one, so the list
  // behind it has to be refreshed as well as the pending queue.
  private refreshAfterApproval(): void {
    this.getApprovals();
    this.scopeList()?.reloadTaList();
  }

  protected readonly toTAStatusText = toTAStatusText;
}
